import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertCircle,
  Gift,
  CreditCard,
  History,
  ArrowRight,
  Settings,
  Clock,
  Trophy,
  ShoppingBag,
} from 'lucide-react';
import { useArcheAdmin } from '@/auth/hooks/useArcheAdmin';
import { useSubscription } from '../hooks';
import { useRewardHistory } from '../hooks';
import { SubscriptionCard } from '../components/SubscriptionCard';
import { ArcheHeader } from '../../../components/ArcheHeader';

export function DashboardPage() {
  const navigate = useNavigate();
  // Server route gate: `requireAdminOrLitcraftAdmin` (docs/api-admin.md).
  const isAdmin = useArcheAdmin('litcraft_admin');
  const { subscription, loading: subLoading, error: subError } = useSubscription();
  const { entries, total, loading: historyLoading, error: historyError } = useRewardHistory();

  const error = subError || historyError;

  return (
    <div className="flex flex-col h-full">
      <ArcheHeader archeId="arche_game_rewards" title="Game Rewards" />
      <div className="max-w-4xl mx-auto p-6 space-y-6 w-full">
        {error && (
          <div className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {subLoading ? (
            <Skeleton className="h-40 rounded-lg" />
          ) : (
            <div
              className="cursor-pointer"
              onClick={() => navigate('/a/arche_game_rewards/subscription')}
            >
              <SubscriptionCard subscription={subscription} />
            </div>
          )}

          {historyLoading ? (
            <Skeleton className="h-40 rounded-lg" />
          ) : (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Gift className="h-4 w-4" /> Recent Rewards
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {entries.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No rewards earned yet.</p>
                ) : (
                  <>
                    {entries.slice(0, 3).map((entry) => (
                      <div key={entry.logId} className="flex items-center justify-between text-sm">
                        <span>{entry.gameEvent}</span>
                        <span className="text-muted-foreground">
                          {entry.rewardsDelivered.length} reward
                          {entry.rewardsDelivered.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    ))}
                    {total > 3 && (
                      <p className="text-xs text-muted-foreground">and {total - 3} more...</p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card
            className="hover:bg-muted/50 transition-colors cursor-pointer"
            onClick={() => navigate('/a/arche_game_rewards/store')}
          >
            <CardContent className="flex items-center justify-between py-6">
              <div className="flex items-center gap-3">
                <ShoppingBag className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Store</p>
                  <p className="text-sm text-muted-foreground">Buy Lit Gems and Lit Passes</p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card
            className="hover:bg-muted/50 transition-colors cursor-pointer"
            onClick={() => navigate('/a/arche_game_rewards/subscription')}
          >
            <CardContent className="flex items-center justify-between py-6">
              <div className="flex items-center gap-3">
                <CreditCard className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Subscription</p>
                  <p className="text-sm text-muted-foreground">Manage your subscription tier</p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card
            className="hover:bg-muted/50 transition-colors cursor-pointer"
            onClick={() => navigate('/a/arche_game_rewards/rewards')}
          >
            <CardContent className="flex items-center justify-between py-6">
              <div className="flex items-center gap-3">
                <History className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Reward History</p>
                  <p className="text-sm text-muted-foreground">View your full reward log</p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card
            className="hover:bg-muted/50 transition-colors cursor-pointer"
            onClick={() => navigate('/a/arche_game_rewards/leaderboards')}
          >
            <CardContent className="flex items-center justify-between py-6">
              <div className="flex items-center gap-3">
                <Trophy className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Leaderboards</p>
                  <p className="text-sm text-muted-foreground">View game rankings</p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card
            className="hover:bg-muted/50 transition-colors cursor-pointer"
            onClick={() => navigate('/a/arche_game_rewards/time-played')}
          >
            <CardContent className="flex items-center justify-between py-6">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Time Played</p>
                  <p className="text-sm text-muted-foreground">Track your gaming time</p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </CardContent>
          </Card>

          {isAdmin && (
            <Card
              className="hover:bg-muted/50 transition-colors cursor-pointer"
              onClick={() => navigate('/a/arche_game_rewards/admin')}
            >
              <CardContent className="flex items-center justify-between py-6">
                <div className="flex items-center gap-3">
                  <Settings className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Admin</p>
                    <p className="text-sm text-muted-foreground">Manage games, rules, and tiers</p>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
